import { Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import {
    PendingProviderSelectionService,
    ProviderLinkService
} from '../../../core/services';
import { PortalApplicationSource } from '../../application/models/application.model';
import { CurrentApplicationService } from '../../application/services/current-application.service';
import { AuthService } from '../../auth/services';

/**
 * Keeps a student who came in through a provider's deep link off the provider search.
 *
 * Someone who started from `/{client}/providers/{id}` came to apply to that one agency, so they
 * are not offered a different one. Hiding "Back to Providers" covers the UI, but the route was
 * still reachable by typing the URL — and the application resolver would have persisted whatever
 * they picked there (LA-22939).
 */
@Injectable({
    providedIn: 'root'
})
export class ProviderSelectionAllowedGuard {
    constructor(
        private readonly _pendingSelection: PendingProviderSelectionService,
        private readonly _currentApplication: CurrentApplicationService,
        private readonly _providerLink: ProviderLinkService,
        private readonly _auth: AuthService,
        private readonly _router: Router
    ) {}

    canActivate(): boolean | UrlTree {
        if (!this.isLockedToOneProvider()) return true;

        return this._router.parseUrl(this.whereTheyBelong());
    }

    /**
     * Two signals, because neither covers the whole flow on its own.
     *
     * The application's recorded pathway is the durable one, but it only exists once the student
     * has an application and it has been loaded — an anonymous student following a deep link has
     * neither. The staged source is written on every deep-link visit, so it covers that gap, but
     * it describes the current visit and is gone in a later session.
     */
    private isLockedToOneProvider(): boolean {
        // Nothing left to protect: a submitted application cannot change providers, and the
        // submitted page invites the student to browse the full list for contact details.
        if (this._currentApplication.latest.value?.isSubmitted) return false;

        return (
            this._pendingSelection.source === PortalApplicationSource.agency ||
            this._currentApplication.latest.value?.portalApplicationSource ===
                PortalApplicationSource.agency
        );
    }

    private whereTheyBelong(): string {
        if (this._auth.isSignedIn)
            return `/${runtimeConfig.clientCode}/application`;

        // Not signed in yet: back to the provider's own page, which is where they came from.
        const providerId = this._providerLink.getProviderIdWithFallback();
        return providerId
            ? `/${runtimeConfig.clientCode}/providers/${providerId}`
            : `/${runtimeConfig.clientCode}/home`;
    }
}
